import React from 'react';
import { ParticipantHubLayout } from './ParticipantHubLayout';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, CircleIcon, ClockIcon, ArrowRightIcon, PlaneIcon, UtensilsIcon, ShirtIcon, SunIcon } from 'lucide-react';
export function FlightPackPage() {
  return <ParticipantHubLayout data-id="element-4579">
      <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-8" data-id="element-4580">
        <div data-id="element-4581">
          <h1 className="font-serif-display text-4xl text-slate-900 mb-2" data-id="element-4582">
            Flight Pack
          </h1>
          <p className="text-slate-500" data-id="element-4583">
            Everything you need to prepare for the Finova Capital Executive
            Summit.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4584">
          <div className="lg:col-span-2 space-y-8" data-id="element-4585">
            {/* Travel Essentials */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 shadow-sm" data-id="element-4586">
              <div className="flex items-center justify-between mb-6" data-id="element-4587">
                <h2 className="font-serif-display text-2xl text-slate-900" data-id="element-4588">
                  Travel Essentials
                </h2>
                <div className="text-sm font-bold text-amber-600 bg-amber-50 px-3 py-1 rounded-full" data-id="element-4589">
                  3/4 Complete
                </div>
              </div>

              <div className="space-y-4" data-id="element-4590">
                {[{
                label: 'Passport & Visa Requirements',
                done: true
              }, {
                label: 'Submit Dietary Preferences',
                done: true
              }, {
                label: 'Book Airport Transfer',
                done: true
              }, {
                label: 'Complete Pre-Retreat Diagnostic',
                done: false
              }].map((item, i) => <div key={i} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors" data-id="element-4591">
                    {item.done ? <CheckCircle2Icon className="w-6 h-6 text-emerald-500 shrink-0" data-id="element-4592" /> : <CircleIcon className="w-6 h-6 text-slate-300 shrink-0" data-id="element-4593" />}
                    <div className={`flex-1 font-medium ${item.done ? 'text-slate-700' : 'text-slate-900'}`} data-id="element-4594">
                      {item.label}
                    </div>
                    {!item.done && <button className="text-sm text-amber-600 font-semibold hover:text-amber-700" data-id="element-4595">
                        Action Required
                      </button>}
                  </div>)}
              </div>
            </motion.div>

            {/* Pre-Reads */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 shadow-sm" data-id="element-4596">
              <h2 className="font-serif-display text-2xl text-slate-900 mb-6" data-id="element-4597">
                Required Pre-Reads
              </h2>
              <div className="space-y-6" data-id="element-4598">
                {[{
                title: 'The Future of Autonomous Agents in FinTech',
                author: 'McKinsey & Company',
                time: '15 min read',
                image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=800&q=80'
              }, {
                title: 'Finova Capital Q3 Performance Memo',
                author: 'David Kim, CSO',
                time: '10 min read',
                image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80'
              }, {
                title: 'AI Integration Frameworks',
                author: 'Harvard Business Review',
                time: '20 min read',
                image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=80'
              }].map((read, i) => <div key={i} className="flex gap-4 group cursor-pointer" data-id="element-4599">
                    <div className="w-24 h-24 rounded-lg overflow-hidden shrink-0" data-id="element-4600">
                      <img src={read.image} alt={read.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-4601" />
                    </div>
                    <div className="flex flex-col justify-center" data-id="element-4602">
                      <h3 className="font-bold text-slate-900 group-hover:text-amber-600 transition-colors mb-1" data-id="element-4603">
                        {read.title}
                      </h3>
                      <div className="text-sm text-slate-500 mb-2" data-id="element-4604">
                        {read.author}
                      </div>
                      <div className="flex items-center gap-1 text-xs font-medium text-slate-400" data-id="element-4605">
                        <ClockIcon className="w-3.5 h-3.5" data-id="element-4606" />
                        {read.time}
                      </div>
                    </div>
                  </div>)}
              </div>
            </motion.div>
          </div>

          <div className="space-y-8" data-id="element-4607">
            {/* Diagnostic CTA */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-[#0B1B3B] rounded-2xl p-6 text-white shadow-lg relative overflow-hidden" data-id="element-4608">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-4609" />
              <div className="relative z-10" data-id="element-4610">
                <h3 className="font-serif-display text-2xl mb-2" data-id="element-4611">
                  Pre-Retreat Diagnostic
                </h3>
                <p className="text-sm text-slate-300 mb-6" data-id="element-4612">
                  Help us personalize your experience and shape the agenda by
                  completing your diagnostic.
                </p>
                <div className="mb-6" data-id="element-4613">
                  <div className="flex justify-between text-xs font-medium mb-2" data-id="element-4614">
                    <span className="text-slate-300" data-id="element-4615">Progress</span>
                    <span className="text-amber-400" data-id="element-4616">40%</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden" data-id="element-4617">
                    <div className="h-full bg-amber-500 rounded-full" style={{
                    width: '40%'
                  }} data-id="element-4618" />
                  </div>
                </div>
                <button className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-[#0B1B3B] rounded-xl font-bold transition-colors flex items-center justify-center gap-2" data-id="element-4619">
                  Continue Diagnostic
                  <ArrowRightIcon className="w-4 h-4" data-id="element-4620" />
                </button>
              </div>
            </motion.div>

            {/* Packing Companion */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-4621">
              <h3 className="font-serif-display text-xl text-slate-900 mb-4" data-id="element-4622">
                Packing Companion
              </h3>
              <div className="space-y-4" data-id="element-4623">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50" data-id="element-4624">
                  <SunIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-4625" />
                  <div data-id="element-4626">
                    <div className="text-sm font-bold text-slate-900" data-id="element-4627">
                      Climate
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-4628">
                      Cancún in October averages 85°F (29°C) with high humidity.
                      Pack light, breathable fabrics.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50" data-id="element-4629">
                  <ShirtIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-4630" />
                  <div data-id="element-4631">
                    <div className="text-sm font-bold text-slate-900" data-id="element-4632">
                      Sessions Attire
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-4633">
                      Resort Casual. Polos, linen shirts, tailored shorts or
                      light trousers.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50" data-id="element-4634">
                  <UtensilsIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-4635" />
                  <div data-id="element-4636">
                    <div className="text-sm font-bold text-slate-900" data-id="element-4637">
                      Dinners
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-4638">
                      Smart Casual. The final Gala Dinner requires cocktail
                      attire.
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </ParticipantHubLayout>;
}